// ---------------------------------------------------------------------------
// UI overlay — DOM chrome around the game canvas: top bar, player list,
// event log, context menus, help, and the join dialog.
// ---------------------------------------------------------------------------

import { getDef } from "../shared/defs";
import type { GameAction } from "./actions";
import { facesVersion, getFace } from "./faces";
import { Settings, type BagEntry } from "./settings";
import type { Replica } from "./replica";

/** Context-menu entry: [label, onClick] or a labeled submenu. */
export type MenuItem = [string, () => void] | { label: string; children: MenuItem[] };

export class UI {
  private topBar: HTMLElement;
  private playersEl: HTMLElement;
  private logEl: HTMLElement;
  private menuEl: HTMLElement | null = null;
  private statusEl: HTMLElement;
  private logLastAt = -1;
  private logRenderedAt = 0;
  onModeToggle: () => void = () => {};

  constructor(private root: HTMLElement, private settings: Settings) {
    this.topBar = el("div", "top-bar");
    const brand = el("div", "brand");
    brand.textContent = "TABLETOP";
    const roomChip = el("button", "chip room-chip");
    roomChip.title = "Copy invite link";
    roomChip.onclick = () => {
      navigator.clipboard?.writeText(location.href);
      this.toast("Invite link copied");
    };
    this.playersEl = el("div", "players");
    const spacer = el("div", "spacer");
    this.statusEl = el("div", "status");
    const modeBtn = el("button", "chip mode-btn");
    modeBtn.textContent = "3D";
    modeBtn.onclick = () => this.onModeToggle();
    const chatBtn = el("button", "chip");
    chatBtn.textContent = "💬";
    chatBtn.title = "Table chat";
    chatBtn.onclick = () => this.openChat();
    const voiceBtn = el("button", "chip") as HTMLButtonElement;
    voiceBtn.textContent = "🎙";
    voiceBtn.title = "Voice chat — this table's Discord channel";
    voiceBtn.onclick = () => void this.openVoice(voiceBtn);
    const accountSlot = el("span", "account-slot");
    const gearBtn = el("button", "chip");
    gearBtn.textContent = "⚙";
    gearBtn.title = "Settings";
    gearBtn.onclick = () => this.settings.toggle(this.root);
    const helpBtn = el("button", "chip");
    helpBtn.textContent = "?";
    helpBtn.onclick = () => this.toggleHelp();
    this.topBar.append(brand, roomChip, this.playersEl, spacer, this.statusEl, accountSlot, voiceBtn, chatBtn, modeBtn, gearBtn, helpBtn);

    this.logEl = el("div", "log");
    root.append(this.topBar, this.logEl);
  }

  setMode(mode: "2d" | "3d"): void {
    const btn = this.topBar.querySelector(".mode-btn")!;
    btn.textContent = mode === "3d" ? "2D" : "3D"; // shows what you switch TO
  }

  setStatus(connected: boolean): void {
    this.statusEl.textContent = connected ? "" : "reconnecting…";
    this.statusEl.classList.toggle("offline", !connected);
  }

  update(state: Replica): void {
    this.tickInspector();
    this.tickBag();
    const chip = this.topBar.querySelector(".room-chip") as HTMLElement;
    if (state.roomId && chip.textContent !== `⌗ ${state.roomId}`) {
      chip.textContent = `⌗ ${state.roomId}`;
    }
    this.roomId = state.roomId;
    // Players. Seats without a hand zone (on tables that have them) watch
    // from the rail — badge them so everyone knows who's actually playing.
    const handSeats = new Set(
      [...state.objs.values()].filter((o) => o.def === "zone.hand").map((o) => o.seat),
    );
    const html = state.players
      .filter((p) => p.connected)
      .map((p) => {
        const spec = handSeats.size > 0 && !handSeats.has(p.seat);
        return `<span class="player${spec ? " spectator" : ""}"><i style="background:${p.color}"></i>${esc(p.name)}${p.id === state.you ? " (you)" : ""}${spec ? `<em class="spec-tag" title="No free hand zone — watching">watching</em>` : ""}</span>`;
      })
      .join("");
    if (this.playersEl.innerHTML !== html) this.playersEl.innerHTML = html;
    // Log: latest 7 lines, fading with age (more, unfaded, while chat is
    // open). The fade only needs a few updates a second — rebuilding the
    // innerHTML every frame is DOM churn.
    const now = performance.now();
    const lines = state.log.slice(this.chatOpen ? -12 : -7);
    const lastAt = lines[lines.length - 1]?.at ?? 0;
    if (lastAt === this.logLastAt && now - this.logRenderedAt < 150) return;
    this.logLastAt = lastAt;
    this.logRenderedAt = now;
    const logHtml = lines
      .map((l) => {
        const age = (now - l.at) / 1000;
        const op = this.chatOpen ? 1 : age > 14 ? 0 : age > 8 ? (14 - age) / 6 : 1;
        const text = l.who ? `<b style="color:${l.color}">${esc(l.who)}:</b> ${esc(l.m)}` : esc(l.m);
        return op > 0.02 ? `<div style="opacity:${op.toFixed(2)}">${text}</div>` : "";
      })
      .join("");
    if (this.logEl.innerHTML !== logHtml) this.logEl.innerHTML = logHtml;
  }

  // ------------------------------------------------------------- menus

  showMenu(items: MenuItem[], x: number, y: number, title?: string): void {
    this.hideMenu();
    const m = el("div", "menu");
    // Submenus render in place (click to descend, ‹ row to come back) —
    // phone-friendly, and the menu never grows past its opening position.
    const render = (list: MenuItem[], heading: string | undefined, parent?: () => void): void => {
      m.innerHTML = "";
      if (parent) {
        const back = el("button", "menu-item menu-back");
        back.textContent = `‹ ${heading ?? "back"}`;
        back.onclick = parent;
        m.append(back);
      } else if (heading) {
        const t = el("div", "menu-title");
        t.textContent = heading;
        m.append(t);
      }
      for (const item of list) {
        const it = el("button", "menu-item");
        if (Array.isArray(item)) {
          const [label, fn] = item;
          it.textContent = label;
          it.onclick = () => {
            this.hideMenu();
            fn();
          };
        } else {
          it.textContent = `${item.label}  ▸`;
          it.classList.add("menu-sub");
          it.onclick = () => render(item.children, item.label, () => render(list, heading, parent));
        }
        m.append(it);
      }
      // Keep the menu on-screen as its height changes between levels.
      const rect = m.getBoundingClientRect();
      m.style.left = `${Math.max(4, Math.min(x, window.innerWidth - rect.width - 8))}px`;
      m.style.top = `${Math.max(4, Math.min(y, window.innerHeight - rect.height - 8))}px`;
    };
    this.root.append(m);
    render(items, title);
    this.menuEl = m;
  }

  hideMenu(): void {
    this.menuEl?.remove();
    this.menuEl = null;
  }

  // -------------------------------------------------------- action bar

  private actionBar: HTMLElement | null = null;
  private actionSig = "";

  /** The per-game action bar (Draw, Mulligan, → Grave…). Empty list hides it. */
  setActions(actions: GameAction[]): void {
    const sig = actions.map((a) => a.id).join(",");
    if (sig === this.actionSig && this.actionBar) return;
    this.actionSig = sig;
    this.actionBar?.remove();
    this.actionBar = null;
    if (actions.length === 0) return;
    const bar = el("div", "action-bar");
    for (const a of actions) {
      const b = el("button", "action-btn");
      const key = a.kb ? this.settings.keyFor(a.kb) : "";
      b.innerHTML = `${esc(a.label)}${key ? `<kbd>${esc(Settings.keyName(key))}</kbd>` : ""}`;
      b.title = a.hint + (key ? ` (${Settings.keyName(key)})` : "");
      b.onclick = () => a.run();
      bar.append(b);
    }
    this.root.append(bar);
    this.actionBar = bar;
  }

  confirm(question: string, yes: () => void): void {
    this.showMenu(
      [
        [`✔ ${question}`, yes],
        ["✖ Cancel", () => {}],
      ],
      window.innerWidth / 2 - 90,
      window.innerHeight / 2 - 40,
    );
  }

  toast(msg: string): void {
    const t = el("div", "toast");
    t.textContent = msg;
    this.root.append(t);
    setTimeout(() => t.classList.add("show"));
    setTimeout(() => {
      t.classList.remove("show");
      setTimeout(() => t.remove(), 400);
    }, 1800);
  }

  // -------------------------------------------------------------- help

  private helpEl: HTMLElement | null = null;

  toggleHelp(): void {
    if (this.helpEl) {
      this.helpEl.remove();
      this.helpEl = null;
      return;
    }
    const h = el("div", "help");
    const key = (a: Parameters<Settings["keyFor"]>[0]) => esc(Settings.keyName(this.settings.keyFor(a) || "—"));
    const wasd = this.settings.data.scheme === "wasd";
    h.innerHTML = `
      <h2>How to play</h2>
      <table>
        <tr><td>drag object</td><td>move it (moves the whole selection)</td></tr>
        <tr><td>drag a deck / stack</td><td><b>pull the top card off</b></td></tr>
        <tr><td>hold ⅓s, then drag (or ⇧-drag)</td><td>move the whole deck</td></tr>
        <tr><td>click an object</td><td>select it (blue outline)</td></tr>
        <tr><td>drop card on card/deck</td><td>stack them</td></tr>
        <tr><td>drop card in a hand zone</td><td>only that player sees it</td></tr>
        <tr><td>double-click deck</td><td>draw 1 to your hand</td></tr>
        <tr><td>double-click card</td><td>flip it</td></tr>
        <tr><td>double-click a die (or ${key("shuffle")})</td><td><b>roll it</b> (server-rolled)</td></tr>
        <tr><td>1–9 over a deck</td><td>draw N to your hand</td></tr>
        <tr><td>${key("panUp")}${key("panLeft")}${key("panDown")}${key("panRight")}</td><td>pan the camera</td></tr>
        ${wasd ? `<tr><td>drag empty table</td><td><b>box-select multiple objects</b></td></tr>` : `<tr><td>drag empty table</td><td>pan the camera</td></tr>`}
        <tr><td>hold ${key("zoomCard")} over a card</td><td><b>magnify it to read the text</b></td></tr>
        <tr><td>right-click a deck</td><td>search it · deal to every player</td></tr>
        <tr><td>${key("sortHand")} / ${key("findCard")}</td><td>sort your hand · find a card in it</td></tr>
        <tr><td>${key("chat")}</td><td>table chat</td></tr>
        <tr><td>${key("flip")} / ${key("rotateLeft")} / ${key("rotateRight")}</td><td>flip · rotate left · right</td></tr>
        <tr><td>${key("shuffle")} / ${key("lock")} / ${key("delete")}</td><td>shuffle · lock · delete</td></tr>
        <tr><td>${key("toggleMode")}</td><td>toggle 2D / 3D</td></tr>
        <tr><td>wheel while dragging</td><td>rotate held object</td></tr>
        <tr><td>right-click</td><td>context menu &amp; spawning</td></tr>
        <tr><td>right-drag / middle-drag</td><td>orbit / pan camera · wheel zoom</td></tr>
      </table>
      <p>Rebind everything in <b>⚙ Settings</b>. Share the invite link (click the room name) to play with others.</p>
      <button class="chip">Close</button>`;
    (h.querySelector("button") as HTMLButtonElement).onclick = () => this.toggleHelp();
    this.root.append(h);
    this.helpEl = h;
  }

  // ---------------------------------------------------------- card zoom

  private zoomEl: HTMLElement | null = null;
  private zoomDef: string | null = null;
  private zoomScaleF = 1;

  get zoomVisible(): boolean {
    return this.zoomEl !== null;
  }

  /** Wheel while the zoom is up: grow/shrink the magnified card. */
  scaleZoom(delta: number): void {
    this.zoomScaleF = Math.min(2.6, Math.max(0.55, this.zoomScaleF * Math.exp(-delta * 0.0012)));
    this.applyZoomScale();
  }

  private applyZoomScale(): void {
    const face = this.zoomEl?.querySelector(".card-zoom-face") as HTMLElement | null;
    if (face) face.style.width = `min(${Math.round(420 * this.zoomScaleF)}px, 92vw)`;
  }

  /** Scryfall stores a lossless PNG tier above "large" — use it when zoomed. */
  private static hiResUrl(url: string): string | null {
    if (!/cards\.scryfall\.io\/(large|normal)\//.test(url)) return null;
    return url.replace(/\/(large|normal)\//, "/png/").replace(/\.jpg(\?|$)/, ".png$1");
  }

  /** Show a magnified, readable view of a card while the zoom key is held. */
  setZoomCard(defId: string | null): void {
    if (defId === this.zoomDef) return;
    this.zoomDef = defId;
    this.zoomEl?.remove();
    this.zoomEl = null;
    if (!defId) return;
    const d = getDef(defId);
    const spec = d.front ?? d.back;
    if (!spec) return;
    const wrap = el("div", "card-zoom");
    let faceEl: HTMLElement;
    const hi = spec.type === "image" ? UI.hiResUrl(spec.url) : null;
    if (spec.type === "image" && hi) {
      // Highest-res art for reading card text; fall back to the cached face.
      const img = document.createElement("img");
      img.src = hi;
      img.onerror = () => {
        img.src = spec.url;
        img.onerror = null;
      };
      faceEl = img;
    } else {
      const face = getFace(spec, d.size[0], d.size[2]);
      const c = document.createElement("canvas");
      c.width = face.width;
      c.height = face.height;
      c.getContext("2d")!.drawImage(face, 0, 0);
      faceEl = c;
    }
    faceEl.className = "card-zoom-face";
    const label = el("div", "card-zoom-name");
    label.textContent = d.name;
    wrap.append(faceEl, label);
    this.root.append(wrap);
    this.zoomEl = wrap;
    this.applyZoomScale();
  }

  // ----------------------------------------------------------- find bar

  private findEl: HTMLElement | null = null;
  private findOnDone: ((keep: boolean) => void) | null = null;

  /**
   * "Find card in hand" overlay: typing highlights matches (onQuery returns
   * the match count). Enter keeps the resulting selection; Escape clears it.
   */
  showFindBar(onQuery: (q: string) => number, onDone: (keep: boolean) => void): void {
    this.closeFindBar(false);
    const bar = el("div", "find-bar");
    const input = document.createElement("input");
    input.type = "text";
    input.placeholder = "find in hand…";
    const count = el("span", "find-count");
    count.textContent = "";
    bar.append(input, count);
    this.root.append(bar);
    this.findEl = bar;
    this.findOnDone = onDone;
    input.oninput = () => {
      const q = input.value.trim();
      const n = onQuery(q);
      count.textContent = q ? `${n} match${n === 1 ? "" : "es"}` : "";
    };
    input.onkeydown = (e) => {
      e.stopPropagation();
      if (e.key === "Enter") this.closeFindBar(true);
      else if (e.key === "Escape") this.closeFindBar(false);
    };
    input.focus();
  }

  closeFindBar(keep: boolean): void {
    if (!this.findEl) return;
    const onDone = this.findOnDone;
    this.findEl.remove();
    this.findEl = null;
    this.findOnDone = null;
    onDone?.(keep);
  }

  get findBarOpen(): boolean {
    return this.findEl !== null;
  }

  // --------------------------------------------------------------- chat

  private chatEl: HTMLElement | null = null;
  /** Wired by main: sends `{t:"chat"}` over the room socket. */
  onChatSend: (m: string) => void = () => {};
  private roomId = "";
  private voiceUrl = "";

  get chatOpen(): boolean {
    return this.chatEl !== null;
  }

  /**
   * Voice chat: fetch this table's Discord voice invite (one channel per
   * room, shared with everyone who clicks) and offer Join / Copy from a
   * menu — the open happens inside a click so popup blockers stay quiet.
   */
  private async openVoice(btn: HTMLButtonElement): Promise<void> {
    if (!this.roomId || btn.disabled) return;
    btn.disabled = true;
    try {
      if (!this.voiceUrl) {
        const res = await fetch(`/api/voice?room=${encodeURIComponent(this.roomId)}`);
        const data = (await res.json().catch(() => ({}))) as { url?: string };
        if (!res.ok || typeof data.url !== "string") {
          this.toast(res.status === 503 ? "Voice chat isn't set up on this server" : "Voice chat unavailable right now");
          return;
        }
        this.voiceUrl = data.url;
      }
      const url = this.voiceUrl;
      const r = btn.getBoundingClientRect();
      this.showMenu(
        [
          ["Join Discord voice ↗", () => { window.open(url, "_blank", "noopener"); }],
          ["Copy voice link", () => {
            navigator.clipboard?.writeText(url);
            this.toast("Voice link copied — paste it to your table");
          }],
        ],
        r.left, r.bottom + 6, "Voice chat — this table",
      );
    } finally {
      btn.disabled = false;
    }
  }

  /** Chat input above the event log. Enter sends (empty Enter closes). */
  openChat(): void {
    if (this.chatEl) {
      this.chatEl.querySelector("input")?.focus();
      return;
    }
    const bar = el("div", "chat-bar");
    const input = document.createElement("input");
    input.type = "text";
    input.maxLength = 300;
    input.placeholder = "say something…";
    bar.append(input);
    this.root.append(bar);
    this.chatEl = bar;
    this.logEl.classList.add("chat-open");
    input.onkeydown = (e) => {
      e.stopPropagation();
      if (e.key === "Enter") {
        const m = input.value.trim();
        if (m) {
          this.onChatSend(m);
          input.value = "";
        } else {
          this.closeChat();
        }
      } else if (e.key === "Escape") {
        this.closeChat();
      }
    };
    input.focus();
  }

  closeChat(): void {
    this.chatEl?.remove();
    this.chatEl = null;
    this.logEl.classList.remove("chat-open");
  }

  // ---------------------------------------------------------- inspector

  private inspectorEl: HTMLElement | null = null;
  private inspectorId: string | null = null;
  private inspectorCells: Array<{ canvas: HTMLCanvasElement; def: string }> = [];
  private inspectorFacesSeen = 0;
  private inspectorSize = 0;
  private inspectorShuffleBox: HTMLInputElement | null = null;
  private inspectorOnClose: ((shuffle: boolean) => void) | null = null;
  /** Def under the pointer inside the inspector (drives the zoom overlay). */
  inspectorHoveredDef: string | null = null;

  /**
   * Contents of a deck/stack (top first) as a scrollable grid.
   * `onAction(index, dest)` uses the SERVER's bottom-based index;
   * `onDragStart(index, ev)` begins dragging that card out onto the table.
   * `shuffleDefault` pre-checks "shuffle on close" (face-down decks).
   */
  showInspector(
    id: string,
    title: string,
    items: Array<{ def: string; name?: string }>,
    onAction: (index: number, dest: "hand" | "table" | "top" | "bottom") => void,
    onDragStart: (index: number, ev: PointerEvent) => void,
    shuffleDefault: boolean,
    onClose: (shuffle: boolean) => void,
  ): void {
    // Refresh in place: remember the checkbox across server-pushed updates.
    const keepShuffle = this.inspectorId === id ? this.inspectorShuffleBox?.checked : undefined;
    if (this.inspectorId === id) {
      this.inspectorEl?.remove(); // rebuild without firing onClose
      this.inspectorEl = null;
    } else {
      this.closeInspector();
    }
    this.inspectorId = id;
    this.inspectorOnClose = onClose;
    this.inspectorCells = [];
    const panel = el("div", "inspector");
    const head = el("div", "inspector-head");
    const t = el("span", "inspector-title");
    t.textContent = `${title} — ${items.length}`;
    const hint = el("span", "inspector-hint");
    hint.textContent = "click: to hand · drag in/out · right-click: more";
    const search = document.createElement("input");
    search.type = "text";
    search.className = "inspector-search";
    search.placeholder = "search…  (t:creature, name)";
    search.oninput = () => {
      const q = search.value.trim().toLowerCase();
      const cells = grid.querySelectorAll<HTMLElement>(".inspector-card");
      cells.forEach((cellEl) => {
        const defId = cellEl.dataset.def;
        if (!q || !defId) {
          cellEl.style.display = "";
          return;
        }
        const d2 = getDef(defId);
        const name = (d2.name ?? "").toLowerCase();
        const type = (d2.meta?.type ?? "").toLowerCase();
        // Scryfall-ish: space-separated terms all must match; t:/type: on type line.
        const ok = q.split(/\s+/).every((term) => {
          const m = /^(t|type):(.+)$/.exec(term);
          if (m) return type.includes(m[2]!);
          return name.includes(term);
        });
        cellEl.style.display = ok ? "" : "none";
      });
    };
    const shuffleLabel = el("label", "inspector-shuffle");
    const shuffleBox = document.createElement("input");
    shuffleBox.type = "checkbox";
    shuffleBox.checked = keepShuffle ?? shuffleDefault;
    shuffleLabel.append(shuffleBox, document.createTextNode(" shuffle on close"));
    this.inspectorShuffleBox = shuffleBox;
    const close = el("button", "chip");
    close.textContent = "✕";
    close.onclick = () => this.closeInspector();
    head.append(t, hint, search, shuffleLabel, close);
    const grid = el("div", "inspector-grid");
    this.inspectorSize = this.settings.data.inspectorCardSize;
    grid.style.setProperty("--card-w", `${this.inspectorSize}px`);
    for (let disp = items.length - 1; disp >= 0; disp--) {
      const item = items[disp]!;
      const d = getDef(item.def);
      const spec = d.front ?? d.back;
      const cell = el("div", "inspector-card");
      cell.dataset.def = item.def;
      cell.title = item.name ?? d.name;
      if (spec) {
        const face = getFace(spec, d.size[0], d.size[2]);
        const c = document.createElement("canvas");
        c.width = face.width;
        c.height = face.height;
        drawFaceRounded(c, face, d);
        this.inspectorCells.push({ canvas: c, def: item.def });
        cell.append(c);
      } else {
        cell.textContent = d.name;
      }
      cell.addEventListener("pointerenter", () => (this.inspectorHoveredDef = item.def));
      cell.addEventListener("pointerleave", () => {
        if (this.inspectorHoveredDef === item.def) this.inspectorHoveredDef = null;
      });
      cell.addEventListener("pointerdown", (ev) => {
        if (ev.button === 0) onDragStart(disp, ev);
      });
      cell.addEventListener("contextmenu", (ev) => {
        ev.preventDefault();
        ev.stopPropagation();
        this.showMenu(
          [
            ["Take to hand", () => onAction(disp, "hand")],
            ["Place on table", () => onAction(disp, "table")],
            ["Move to top", () => onAction(disp, "top")],
            ["Move to bottom", () => onAction(disp, "bottom")],
          ],
          ev.clientX, ev.clientY, item.name ?? d.name,
        );
      });
      grid.append(cell);
    }
    panel.append(head, grid);
    this.root.append(panel);
    this.inspectorEl = panel;
    this.inspectorFacesSeen = facesVersion();
  }

  /** Async face loads for bag thumbnails (per frame). */
  private tickBag(): void {
    if (!this.bagEl || facesVersion() === this.bagFacesSeen) return;
    this.bagFacesSeen = facesVersion();
    for (const t of this.bagThumbs) {
      const d = getDef(t.def);
      const spec = d.front ?? d.back;
      if (spec) drawFaceRounded(t.canvas, getFace(spec, d.size[0], d.size[2]), d);
    }
  }

  /** Async face loads + live size changes for the open panel (per frame). */
  private tickInspector(): void {
    if (!this.inspectorEl) return;
    if (facesVersion() !== this.inspectorFacesSeen) {
      this.inspectorFacesSeen = facesVersion();
      for (const cell of this.inspectorCells) {
        const d = getDef(cell.def);
        const spec = d.front ?? d.back;
        if (!spec) continue;
        drawFaceRounded(cell.canvas, getFace(spec, d.size[0], d.size[2]), d);
      }
    }
    if (this.settings.data.inspectorCardSize !== this.inspectorSize) {
      this.inspectorSize = this.settings.data.inspectorCardSize;
      const grid = this.inspectorEl.querySelector(".inspector-grid") as HTMLElement;
      grid?.style.setProperty("--card-w", `${this.inspectorSize}px`);
    }
  }

  isOverInspector(x: number, y: number): boolean {
    if (!this.inspectorEl) return false;
    const r = this.inspectorEl.getBoundingClientRect();
    return x >= r.left && x <= r.right && y >= r.top && y <= r.bottom;
  }

  closeInspector(id?: string): void {
    if (id !== undefined && id !== this.inspectorId) return;
    const onClose = this.inspectorOnClose;
    const shuffle = this.inspectorShuffleBox?.checked ?? false;
    this.inspectorEl?.remove();
    this.inspectorEl = null;
    this.inspectorId = null;
    this.inspectorCells = [];
    this.inspectorShuffleBox = null;
    this.inspectorOnClose = null;
    this.inspectorHoveredDef = null;
    onClose?.(shuffle);
  }

  /** Escape: close transient chrome (menus, panels, zoom, help). */
  escapeCloseAll(): void {
    this.hideMenu();
    this.closeInspector();
    this.closeFindBar(false);
    this.closeChat();
    this.setZoomCard(null);
    if (this.helpEl) this.toggleHelp();
    this.bagEl?.remove();
    this.bagEl = null;
  }

  // -------------------------------------------------------------- bags

  private bagEl: HTMLElement | null = null;
  private bagThumbs: Array<{ canvas: HTMLCanvasElement; def: string }> = [];
  private bagFacesSeen = 0;

  /** Saved-objects bag: spawn or delete entries. */
  showBagPanel(settings: Settings, onSpawn: (data: BagEntry["data"]) => void): void {
    this.bagEl?.remove();
    this.bagThumbs = [];
    this.bagFacesSeen = facesVersion();
    const panel = el("div", "bag-panel");
    const head = el("div", "inspector-head");
    const t = el("span", "inspector-title");
    t.textContent = "Bag — saved objects";
    const hint = el("span", "inspector-hint");
    hint.textContent = settings.data.bags.length ? "spawns at the spot you right-clicked" : "right-click an object → “Save to bag…”";
    const close = el("button", "chip");
    close.textContent = "✕";
    close.onclick = () => {
      panel.remove();
      this.bagEl = null;
    };
    head.append(t, hint, close);
    panel.append(head);
    const list = el("div", "bag-list");
    for (const [i, entry] of settings.data.bags.entries()) {
      const rowEl = el("div", "bag-row");
      // Thumbnail of what's inside (the object that was right-clicked).
      const thumbWrap = el("span", "bag-thumb");
      if (entry.thumbDef) {
        const d = getDef(entry.thumbDef);
        const spec = d.front ?? d.back;
        if (spec) {
          const face = getFace(spec, d.size[0], d.size[2]);
          const c = document.createElement("canvas");
          c.width = face.width;
          c.height = face.height;
          drawFaceRounded(c, face, d);
          this.bagThumbs.push({ canvas: c, def: entry.thumbDef });
          thumbWrap.append(c);
        }
      }
      const name = el("span", "bag-name");
      name.textContent = entry.name;
      const when = el("span", "bag-when");
      when.textContent = new Date(entry.at).toLocaleDateString();
      const spawn = el("button", "chip");
      spawn.textContent = "Spawn";
      spawn.onclick = () => {
        onSpawn(entry.data);
        panel.remove();
        this.bagEl = null;
      };
      const del = el("button", "chip");
      del.textContent = "✕";
      del.title = "Delete this entry";
      del.onclick = () => {
        settings.data.bags.splice(i, 1);
        settings.save();
        this.showBagPanel(settings, onSpawn);
      };
      rowEl.append(thumbWrap, name, when, spawn, del);
      list.append(rowEl);
    }
    if (settings.data.bags.length === 0) {
      const empty = el("div", "bag-empty");
      empty.textContent = "Nothing bagged yet.";
      list.append(empty);
    }
    panel.append(list);
    this.root.append(panel);
    this.bagEl = panel;
  }

  get openInspectorId(): string | null {
    return this.inspectorId;
  }

  // ------------------------------------------------------ counter chips

  /** Proxy-Table chip palette + custom label. */
  showCounterChipMenu(x: number, y: number, onPick: (color: string, label: string) => void): void {
    const COLORS: Array<[string, string]> = [
      ["Black", "#26272e"], ["Blue", "#21406e"], ["Red", "#7a2230"],
      ["Green", "#234a2c"], ["Purple", "#4a2a6e"],
    ];
    this.showMenu(
      [
        ...COLORS.map(([name, color]): [string, () => void] => [
          `● ${name} chip`, () => onPick(color, ""),
        ]),
        ["Labeled chip…", () => {
          const label = prompt("Chip label (up to 6 chars):", "⚡") ?? "";
          const color = prompt("Hex color:", "#26272e") ?? "#26272e";
          onPick(color, label);
        }],
      ],
      x, y, "Counter chips",
    );
  }

  // ------------------------------------------------------- scry / reveal

  private scryEl: HTMLElement | null = null;

  /**
   * Scry/surveil resolver: cards shown top-first; click toggles a card
   * between "keep on top" and "bottom/graveyard". Order among kept = shown.
   */
  showScryPanel(
    mode: "scry" | "surveil",
    items: Array<{ def: string; name?: string }>,
    onResolve: (keepTop: number[], away: number[]) => void,
  ): void {
    this.scryEl?.remove();
    const away = new Set<number>();
    const panel = el("div", "inspector scry-panel");
    const head = el("div", "inspector-head");
    const t = el("span", "inspector-title");
    t.textContent = mode === "scry" ? "Scry — click cards to send to the bottom" : "Surveil — click cards to send to the graveyard";
    const done = el("button", "chip");
    done.textContent = "Done";
    head.append(t, done);
    const grid = el("div", "inspector-grid");
    grid.style.setProperty("--card-w", `${this.settings.data.inspectorCardSize}px`);
    items.forEach((item, i) => {
      const d = getDef(item.def);
      const spec = d.front ?? d.back;
      const cell = el("div", "inspector-card scry-card");
      cell.title = item.name ?? d.name;
      if (spec) {
        const face = getFace(spec, d.size[0], d.size[2]);
        const c = document.createElement("canvas");
        c.width = face.width;
        c.height = face.height;
        drawFaceRounded(c, face, d);
        cell.append(c);
      }
      const tag = el("div", "scry-tag");
      tag.textContent = "top";
      cell.append(tag);
      cell.onclick = () => {
        if (away.has(i)) away.delete(i);
        else away.add(i);
        cell.classList.toggle("scry-away", away.has(i));
        tag.textContent = away.has(i) ? (mode === "scry" ? "bottom" : "grave") : "top";
      };
      grid.append(cell);
    });
    done.onclick = () => {
      panel.remove();
      this.scryEl = null;
      const keepTop = items.map((_, i) => i).filter((i) => !away.has(i));
      onResolve(keepTop, [...away]);
    };
    panel.append(head, grid);
    this.root.append(panel);
    this.scryEl = panel;
  }

  /** Room-wide reveal viewer (read-only). */
  showRevealPanel(who: string, items: Array<{ def: string; name?: string }>): void {
    const panel = el("div", "inspector reveal-panel");
    const head = el("div", "inspector-head");
    const t = el("span", "inspector-title");
    t.textContent = `${who} revealed`;
    const close = el("button", "chip");
    close.textContent = "✕";
    close.onclick = () => panel.remove();
    head.append(t, close);
    const grid = el("div", "inspector-grid");
    grid.style.setProperty("--card-w", `${this.settings.data.inspectorCardSize}px`);
    for (const item of items) {
      const d = getDef(item.def);
      const spec = d.front ?? d.back;
      const cell = el("div", "inspector-card");
      cell.title = item.name ?? d.name;
      if (spec) {
        const face = getFace(spec, d.size[0], d.size[2]);
        const c = document.createElement("canvas");
        c.width = face.width;
        c.height = face.height;
        drawFaceRounded(c, face, d);
        cell.append(c);
      }
      grid.append(cell);
    }
    panel.append(head, grid);
    this.root.append(panel);
    setTimeout(() => panel.remove(), 30000); // auto-dismiss eventually
  }

  // --------------------------------------------------------- life panel

  private lifeEl: HTMLElement | null = null;
  private lifeExpanded = false;

  /** Central life + commander damage tracker (shown when gameState exists). */
  updateLifePanel(
    state: Replica,
    life: number[],
    cmd: number[][],
    onLife: (seat: number, delta: number) => void,
    onCmd: (from: number, to: number, delta: number) => void,
  ): void {
    if (!this.lifeEl) {
      this.lifeEl = el("div", "life-panel");
      this.root.append(this.lifeEl);
    }
    const seatName = (seat: number) =>
      state.players.find((p) => p.seat === seat)?.name ?? `Seat ${seat + 1}`;
    const seatColor = (seat: number) =>
      state.players.find((p) => p.seat === seat)?.color ?? "#666";
    const rows = life
      .map((lf, seat) => {
        const mine = seat === state.seat;
        return `<div class="life-row${mine ? " mine" : ""}">
          <i style="background:${seatColor(seat)}"></i>
          <span class="life-name">${escapeHtml(seatName(seat))}</span>
          <button data-life="${seat}:-1" class="life-btn">−</button>
          <span class="life-total">${lf}</span>
          <button data-life="${seat}:1" class="life-btn">+</button>
        </div>`;
      })
      .join("");
    const hasCmd = cmd.length > 0; // duel tables track life but not cmdr damage
    let cmdHtml = "";
    if (this.lifeExpanded && hasCmd) {
      cmdHtml = `<div class="cmd-grid"><div class="cmd-head">CMDR damage (row → col)</div>` +
        life.map((_, from) =>
          `<div class="cmd-row"><i style="background:${seatColor(from)}"></i>` +
          life.map((_, to) => from === to
            ? `<span class="cmd-cell cmd-self">·</span>`
            : `<span class="cmd-cell" data-cmd="${from}:${to}">${cmd[from]?.[to] ?? 0}</span>`,
          ).join("") + `</div>`,
        ).join("") + `<div class="cmd-hint">click +1 · right-click −1 · 21 kills</div></div>`;
    }
    const toggle = hasCmd ? `<button class="chip life-toggle">${this.lifeExpanded ? "▴ cmdr" : "▾ cmdr"}</button>` : "";
    const html = `${rows}${toggle}${cmdHtml}`;
    if (this.lifeEl.dataset.html !== html) {
      this.lifeEl.dataset.html = html;
      this.lifeEl.innerHTML = html;
      this.lifeEl.querySelectorAll<HTMLElement>("[data-life]").forEach((b) => {
        const [seat, delta] = b.dataset.life!.split(":").map(Number);
        b.onclick = (e) => onLife(seat!, (e as MouseEvent).shiftKey ? delta! * 5 : delta!);
      });
      const toggleEl = this.lifeEl.querySelector<HTMLElement>(".life-toggle");
      if (toggleEl) {
        toggleEl.onclick = () => {
          this.lifeExpanded = !this.lifeExpanded;
          this.lifeEl!.dataset.html = "";
        };
      }
      this.lifeEl.querySelectorAll<HTMLElement>("[data-cmd]").forEach((c) => {
        const [from, to] = c.dataset.cmd!.split(":").map(Number);
        c.onclick = () => onCmd(from!, to!, 1);
        c.oncontextmenu = (e) => {
          e.preventDefault();
          onCmd(from!, to!, -1);
        };
      });
    }
  }

  hideLifePanel(): void {
    this.lifeEl?.remove();
    this.lifeEl = null;
  }

  // --------------------------------------------------------- join dialog

  askName(defaultName: string): Promise<string> {
    return new Promise((resolve) => {
      const wrap = el("div", "join");
      wrap.innerHTML = `
        <div class="join-card">
          <h1>TABLETOP <span>online</span></h1>
          <p>A shared table. Decks, chips, hands — with friends, in the browser.</p>
          <input type="text" maxlength="24" placeholder="Your name" value="${esc(defaultName)}" />
          <button>Sit down at the table</button>
        </div>`;
      const input = wrap.querySelector("input") as HTMLInputElement;
      const btn = wrap.querySelector("button") as HTMLButtonElement;
      const go = () => {
        const name = input.value.trim() || "Guest";
        wrap.remove();
        resolve(name);
      };
      btn.onclick = go;
      input.onkeydown = (e) => {
        if (e.key === "Enter") go();
      };
      this.root.append(wrap);
      input.focus();
      input.select();
    });
  }
}

/** Copy a face canvas, clipped to the def's corner radius (kills white corners). */
function drawFaceRounded(target: HTMLCanvasElement, face: HTMLCanvasElement, d: ReturnType<typeof getDef>): void {
  const g = target.getContext("2d")!;
  g.clearRect(0, 0, target.width, target.height);
  g.save();
  const r = ((d.cornerRadius ?? 0) / d.size[0]) * target.width;
  if (r > 0) {
    g.beginPath();
    g.moveTo(r, 0);
    g.arcTo(target.width, 0, target.width, target.height, r);
    g.arcTo(target.width, target.height, 0, target.height, r);
    g.arcTo(0, target.height, 0, 0, r);
    g.arcTo(0, 0, target.width, 0, r);
    g.closePath();
    g.clip();
  }
  g.drawImage(face, 0, 0, target.width, target.height);
  g.restore();
}

function el(tag: string, cls: string): HTMLElement {
  const e = document.createElement(tag);
  e.className = cls;
  return e;
}

function escapeHtml(s: string): string {
  return esc(s);
}

function esc(s: string): string {
  return s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]!));
}
